import { CSSProperties, DragEvent, MouseEvent } from "react"
import { faArrowsRotate, faCircleCheck, faGripVertical } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { useAppSelector } from "@/core/root-reducer"
import establishmentStore from "@/entities/establishment/model/establishment.store"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import populationStore from "@/entities/population/model/population.store"
import { PreventionAction } from "@/entities/prevention-action/model/prevention-action.types"
import siteStore from "@/entities/site/model/site.store"

interface Props {
  action: PreventionAction
  className?: string
  displayStatus?: boolean
  displayPeriodicStyle?: boolean
  displaySubtitle?: boolean
  displayDragHandle?: boolean
  draggable?: boolean
  onClick?: (event: MouseEvent<HTMLDivElement>) => void
  onDragEnd?: () => void
  onDragStart?: (event: DragEvent<HTMLDivElement>) => void
  onResizeEndStart?: (event: DragEvent<HTMLDivElement>) => void
  onResizeStartStart?: (event: DragEvent<HTMLDivElement>) => void
  showEndResizeHandle?: boolean
  showStartResizeHandle?: boolean
  style?: CSSProperties
  title?: string
  titleClassName?: string
}

export default function PreventionActionCalendarItem({
  action,
  className = "",
  displayStatus = true,
  displayPeriodicStyle = true,
  displaySubtitle = true,
  displayDragHandle = false,
  draggable = false,
  onClick,
  onDragEnd,
  onDragStart,
  onResizeEndStart,
  onResizeStartStart,
  showEndResizeHandle = false,
  showStartResizeHandle = false,
  style,
  title,
  titleClassName = "font-bold"
}: Props) {
  const isDone = displayStatus && action.cStatus === "done"
  const isPeriodic = displayPeriodicStyle && !isDone && action.refreshDelay !== null
  const risk = useAppSelector(physicalRiskStore.selectors.list).find((item) => item.id === action.PhysicalRiskId)
  const sites = useAppSelector(siteStore.selectors.list)
  const populations = useAppSelector(populationStore.selectors.list)
  const establishment = useAppSelector(establishmentStore.selectors.active)
  const riskSites = risk ? sites.filter((site) => risk.siteIds.includes(site.id)) : []
  //Une mesure RPS n'a pas de risque : son perimetre est celui de ses fonctions
  const riskPopulations = populations.filter((population) =>
    (risk ? risk.populationIds : action.populationIds).includes(population.id)
  )
  const perimeter = [
    riskSites.length > 1 ? `${riskSites.length} lieux` : riskSites[0]?.name,
    riskPopulations.length > 1 ? `${riskPopulations.length} fonctions` : riskPopulations[0]?.name
  ].filter(Boolean)

  if (!perimeter.length && establishment?.name) perimeter.push(establishment.name)

  return (
    <div
      className={`_hover relative overflow-hidden px-[14px] py-[4px] text-sm ${isPeriodic ? "" : "rounded-[12px]"} ${isDone ? "grey-t" : ""} ${className}`}
      draggable={draggable}
      style={{
        backgroundColor: isPeriodic ? "transparent" : isDone ? "#F1F5F9" : risk?.cValueEffectiveColor || "#64748B",
        color: isPeriodic ? risk?.cValueEffectiveColor || "#64748B" : isDone ? undefined : "white",
        ...style
      }}
      onClick={onClick}
      onDragEnd={onDragEnd}
      onDragStart={onDragStart}>
      {displayDragHandle && (
        <FontAwesomeIcon
          className="absolute left-[6px] top-1/2 -translate-y-1/2 text-base text-white/60"
          icon={faGripVertical}
        />
      )}
      {showStartResizeHandle && (
        <div
          className="absolute inset-y-0 left-0 z-[4] w-[14px] cursor-ew-resize"
          draggable
          title="Modifier la date de début"
          onClick={(event) => event.stopPropagation()}
          onDragEnd={onDragEnd}
          onDragStart={onResizeStartStart}>
          <span className="absolute inset-y-[10px] left-[6px] w-[2px] rounded-full bg-white/45" />
        </div>
      )}
      <div className={`flex h-full min-w-0 items-center gap-10 ${displayDragHandle ? "pl-[10px]" : ""}`}>
        {isDone && <FontAwesomeIcon className="shrink-0 text-base" icon={faCircleCheck} />}
        {isPeriodic && <FontAwesomeIcon className="shrink-0 text-base" icon={faArrowsRotate} />}
        <div className="min-w-0 flex-1">
          <div className={`truncate ${titleClassName}`}>{title || action.name}</div>
          {displaySubtitle && <div className="truncate font-normal">{perimeter.join(" · ")}</div>}
        </div>
      </div>
      {showEndResizeHandle && (
        <div
          className="absolute inset-y-0 right-0 z-[4] w-[14px] cursor-ew-resize"
          draggable
          title="Modifier la date de fin"
          onClick={(event) => event.stopPropagation()}
          onDragEnd={onDragEnd}
          onDragStart={onResizeEndStart}>
          <span className="absolute inset-y-[10px] right-[6px] w-[2px] rounded-full bg-white/45" />
        </div>
      )}
    </div>
  )
}
